import BaseWrapper from "../../../components/ui/BaseWrapper";
import BaseEditor from "../../../components/ui/BaseEditor";
import BaseTextField from "../../../components/ui/BaseTextField";
import PhoneInput from "react-phone-input-2";
import { FormHelperText } from "@mui/material";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { fetchCompanyBranches } from "../../../services/api/branch";
import { useQuery } from "@tanstack/react-query";
import { fetchTeams } from "../../../services/api/teams";
import { fetchDepartments } from "../../../services/api/departments";
import useAuth from "../../../hooks/useAuth";
import { UserType } from "../../../utils/enum";
import {
  ANNUAL_REVENUE_OPTIONS,
  customerLabel,
  normalizeRichText,
  toEditorHtml,
} from "../../../utils/helpers";

// Client form fields. Each screen lists the fields it shows, in order, inside a
// ClientFormGrid; nothing here decides which fields a screen gets.

const SERVICE_DELIVERY_DEPARTMENT = "service delivery";
const OPERATIONS_DEPARTMENT = "operations";

const CELL = "lg:mr-10 lg:col-span-1 col-span-2";
const LABEL = "flex items-baseline min-h-[26px] mb-1.5 text-[13px] font-normal text-[#686868]";

const withLinkedOption = (options, linked) => {
  if (!linked?.id) return options;
  if (options.some((option) => option.value === linked.id)) return options;
  return [...options, { label: linked.name, value: linked.id }];
};

const toOptions = (response) =>
  response?.data?.data?.map((item) => ({ label: item.name, value: item.id })) || [];

const errorOf = (formik, name) => formik.touched[name] && formik.errors[name];

// What every field needs: the form, whether it is read-only, and what to call the record.
export const useClientFormContext = ({
  formik,
  isEditable = true,
  isEditMode = true,
  lockedFields = [],
  // Pages where a website was never collected (public customer signup) show the
  // field as optional rather than demanding one the account may not have.
  isWebsiteOptional = false,
  // Answers first given at signup; the customer can correct them on their profile.
  showSignupAnswers = false,
  recordLabel,
  linkedBranch,
  linkedTeam,
  linkedSecondaryTeam,
}) => {
  const { authRole } = useAuth();
  const readOnly = !isEditable && isEditMode;

  return {
    formik,
    readOnly,
    isLocked: (name) => readOnly || lockedFields.includes(name),
    recordLabel: recordLabel ?? customerLabel(authRole),
    // Branch, teams, billing terms and policies are internal to the servicing company.
    showInternal: authRole !== UserType.CUSTOMER,
    linkedBranch,
    linkedTeam,
    linkedSecondaryTeam,
  };
};

export const ClientFormGrid = ({ ctx, compactTop = false, children }) => (
  <BaseWrapper>
    <div className="pb-8 ">
      <form
        onSubmit={ctx.formik.handleSubmit}
        className={`grid grid-cols-3 gap-y-5 text-black ${compactTop ? "mt-2" : "mt-7"}`}
      >
        {children}
      </form>
    </div>
  </BaseWrapper>
);

const TextField = ({ ctx, name, label, requirement, inputProps, type, disabled, hint }) => (
  <div className={CELL}>
    <BaseTextField
      fullHeight={false}
      fullWidth
      labelShrink={false}
      id={name}
      name={name}
      label={label}
      type={type}
      hint={hint}
      requirement={requirement}
      optional={requirement ? undefined : false}
      value={ctx.formik.values?.[name]}
      onChange={ctx.formik.handleChange}
      onBlur={ctx.formik.handleBlur}
      isDefaultField={false}
      inputProps={inputProps}
      error={errorOf(ctx.formik, name)}
      helperText={errorOf(ctx.formik, name)}
      disabled={disabled ?? ctx.isLocked(name)}
    />
  </div>
);

const SelectField = ({ ctx, name, label, options, requirement }) => (
  <div className={CELL}>
    <BaseAutoSelect
      fullHeight={false}
      fullWidth
      labelShrink={false}
      id={name}
      name={name}
      label={label}
      options={options}
      requirement={requirement}
      value={ctx.formik.values?.[name]}
      onChange={ctx.formik.handleChange}
      onBlur={ctx.formik.handleBlur}
      error={errorOf(ctx.formik, name)}
      helperText={errorOf(ctx.formik, name)}
      disabled={ctx.isLocked(name)}
    />
  </div>
);

const PhoneField = ({ ctx, name, label, required, compact = false }) => (
  <div className={CELL}>
    <label
      htmlFor={name}
      className={compact ? "block text-sm font-sm mb-1 !text-[#525354]" : LABEL}
    >
      {label}
      {required && (
        <span className="text-red-500 ml-0.5" aria-hidden="true">
          *
        </span>
      )}
    </label>
    <div className={`relative w-full ${compact ? "mt-3" : ""}`}>
      <PhoneInput
        inputClass={`!w-full ${compact ? "!h-[42px] !text-sm" : "!h-[49px] !text-[13px]"} !pl-12 !pr-3 !font-light
          !text-black !border !rounded-md !shadow-md
          ${ctx.isLocked(name) ? "!bg-[#F6F6F6] !cursor-not-allowed !text-gray-500" : "!bg-white"}
          ${errorOf(ctx.formik, name) && "!border-gray-300"}
          focus:!outline-none focus:!ring-1 focus:!ring-orange-400`}
        buttonClass="!border-none !bg-transparent"
        containerClass="!w-full"
        specialLabel=""
        country={"au"}
        value={ctx.formik.values[name]}
        onChange={(value) => ctx.formik.setFieldValue(name, value)}
        onBlur={() => ctx.formik.setFieldTouched(name, true)}
        inputProps={{ name, id: name, type: "tel" }}
        disabled={ctx.isLocked(name)}
      />
      {errorOf(ctx.formik, name) && (
        <FormHelperText error>{ctx.formik.errors[name]}</FormHelperText>
      )}
    </div>
  </div>
);

const FileField = ({ ctx, name, label, required, accept }) => {
  const file = ctx.formik.values?.[name];
  const inputId = `${name}Input`;

  return (
    <div className={CELL}>
      <label htmlFor={name} className={LABEL}>
        {label}
        {required && (
          <span className="text-red-500 ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div
        className={`relative w-full rounded p-[14px] pr-12 text-[13px] font-light leading-[19px] shadow-[1px_1px_4px_1px_#00000021] ${
          ctx.readOnly
            ? "bg-[#f5f5f5] text-[#9f9f9f] cursor-not-allowed"
            : "bg-white text-[#686868] cursor-pointer"
        }`}
      >
        <span className="block truncate">
          {(file?.url && (
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              title={file?.fileName}
              className="cursor-pointer block truncate text-blue-500 hover:underline font-300"
            >
              {file?.fileName}
            </a>
          )) ||
            file?.name ||
            file?.fileName ||
            "Choose File"}
        </span>
        <i
          onClick={() => document.getElementById(inputId).click()}
          className="fa-solid fa-paperclip text-primary text-xl absolute right-3 top-1/2 -translate-y-1/2"
        />
        <input
          type="file"
          id={inputId}
          name={name}
          accept={accept}
          onChange={(event) => ctx.formik.setFieldValue(name, event.target.files[0])}
          onBlur={ctx.formik.handleBlur}
          className="hidden"
          disabled={ctx.readOnly}
        />
      </div>
      {required && errorOf(ctx.formik, name) && (
        <p className="text-[#E20000] text-xs mt-1">{ctx.formik.errors[name]}</p>
      )}
    </div>
  );
};

// ---- Identity ----

export const NameField = ({ ctx }) => (
  <TextField ctx={ctx} name="clientName" label={`${ctx.recordLabel} Name`} requirement="required" />
);

export const EmailField = ({ ctx }) => (
  <TextField
    ctx={ctx}
    name="email"
    label="Email"
    requirement="required"
    inputProps={{ maxLength: 50 }}
    // the company's own address, independent of the login the account signs in with
    disabled={ctx.isLocked("email")}
  />
);

export const ClientPhoneField = ({ ctx }) => (
  <PhoneField ctx={ctx} name="phone" label="Phone" required />
);

export const TeamEmailField = ({ ctx }) => (
  <TextField
    ctx={ctx}
    name="teamEmail"
    label="Team Email"
    hint="from the service delivery team"
    disabled
  />
);

export const ContactPersonField = ({ ctx }) => (
  <TextField ctx={ctx} name="manager" label="Contact Person" requirement="required" />
);

// Chosen at customer signup.
export const AnnualRevenueField = ({ ctx }) => (
  <SelectField
    ctx={ctx}
    name="companyAnnualRevenue"
    label="Annual Revenue"
    options={ANNUAL_REVENUE_OPTIONS}
    requirement="required"
  />
);

export const WebsiteField = ({ ctx }) => (
  <TextField
    ctx={ctx}
    name="website"
    label="Website"
    requirement="optional"
    inputProps={{ maxLength: 100 }}
  />
);

export const LogoField = ({ ctx }) => (
  <FileField ctx={ctx} name="clientLogo" label={`${ctx.recordLabel} Logo`} accept="image/*" />
);

// ---- Branch & teams ----

const useDepartmentTeams = (departmentName) => {
  const { data: departmentsData } = useQuery({
    queryKey: ["departmentsData"],
    queryFn: () => fetchDepartments(),
  });
  const departmentId = (departmentsData?.data?.data || []).find((dept) =>
    dept.name?.trim().toLowerCase().includes(departmentName),
  )?.id;

  const { data } = useQuery({
    queryKey: [
      departmentName === OPERATIONS_DEPARTMENT ? "secondaryTeamsData" : "teamsData",
      departmentId,
    ],
    queryFn: () => fetchTeams({ departmentId }),
    enabled: !!departmentId,
  });
  return toOptions(data);
};

export const BranchField = ({ ctx }) => {
  const { data } = useQuery({
    queryKey: ["branchesData"],
    queryFn: () => fetchCompanyBranches(),
  });
  // Viewers who cannot list branches (admins) still need the linked one named.
  const options = withLinkedOption(toOptions(data), ctx.linkedBranch);

  return (
    <SelectField
      ctx={ctx}
      name="branchId"
      label="Branch Name"
      options={options}
      requirement="required"
    />
  );
};

export const ServiceTeamField = ({ ctx }) => {
  const options = withLinkedOption(useDepartmentTeams(SERVICE_DELIVERY_DEPARTMENT), ctx.linkedTeam);
  return (
    <SelectField
      ctx={ctx}
      name="teamId"
      label="Service Delivery Teams"
      options={options}
      requirement="required"
    />
  );
};

export const OperationsTeamField = ({ ctx }) => {
  const options = withLinkedOption(
    useDepartmentTeams(OPERATIONS_DEPARTMENT),
    ctx.linkedSecondaryTeam,
  );
  return (
    <SelectField
      ctx={ctx}
      name="secondaryTeamId"
      label="Operations Teams"
      options={options}
      requirement="required"
    />
  );
};

// ---- Registration ----

export const RegistrationNumberField = ({ ctx }) => (
  <TextField
    ctx={ctx}
    name="registrationNumber"
    label="Registration Number"
    requirement="required"
    inputProps={{ maxLength: 50 }}
  />
);

export const RegistrationCertificateField = ({ ctx }) => (
  <FileField
    ctx={ctx}
    name="registrationCertificate"
    label="Registration Certificate"
    required
    accept=".pdf,.doc,.docx,image/*"
  />
);

export const TaxRegistrationField = ({ ctx }) => (
  <TextField ctx={ctx} name="taxRegistrationNumber" label="VAT/GST Registration" />
);

// ---- Address ----

export const AddressFields = ({ ctx }) => (
  <>
    <TextField ctx={ctx} name="address" label="Address" requirement="required" />
    <TextField ctx={ctx} name="city" label="City" requirement="required" />
    <TextField ctx={ctx} name="state" label="State" />
    <TextField
      ctx={ctx}
      name="zipCode"
      label="Zip / Post Code"
      requirement="required"
      inputProps={{ maxLength: 10 }}
    />
    <TextField ctx={ctx} name="country" label="Country" requirement="required" />
  </>
);

// ---- Accounts ----

export const AccountsFields = ({ ctx }) => (
  <>
    <TextField ctx={ctx} name="accountsPerson" label="Accounts Person" requirement="required" />
    <TextField ctx={ctx} name="accountsEmail" label="Accounts Email" requirement="required" />
    <PhoneField ctx={ctx} name="accountsPhone" label="Accounts Phone" compact />
  </>
);

export const BillingTermsFields = ({ ctx }) => (
  <>
    <TextField ctx={ctx} name="paymentTerms" label="Payment Terms" />
    <SelectField
      ctx={ctx}
      name="taxType"
      label="Tax Type"
      options={[
        { label: "GST", value: "GST" },
        { label: "VAT", value: "VAT" },
      ]}
    />
    <TextField
      ctx={ctx}
      name="taxPercentage"
      label="Tax Percentage"
      type="float"
      inputProps={{ maxLength: 6 }}
    />
  </>
);

// ---- Policies ----

export const PoliciesField = ({ ctx }) => (
  <div className="col-span-3 pb-2">
    <p className="font-normal mb-2">
      {ctx.recordLabel}&apos;s Policies
      <span className="text-red-500 ml-0.5" aria-hidden="true">
        *
      </span>
    </p>
    <div
      className={`rounded-md bg-white shadow-md [&_.ql-editor]:min-h-[200px] [&_.ql-editor]:max-h-[360px] [&_.ql-editor]:overflow-y-auto [&_.ql-editor]:px-4 [&_.ql-editor]:py-3 ${
        ctx.readOnly ? "opacity-70 pointer-events-none" : ""
      }`}
      onBlur={() => ctx.formik.setFieldTouched("policies", true)}
    >
      <BaseEditor
        value={toEditorHtml(ctx.formik.values?.policies)}
        // only the user's own edits count, so opening a form never marks it changed
        onChange={(html, delta, source) => {
          if (source === "user") {
            ctx.formik.setFieldValue("policies", normalizeRichText(html), false);
          }
        }}
        readOnly={ctx.readOnly}
        placeholder={`${ctx.recordLabel}'s policies`}
      />
    </div>
    {errorOf(ctx.formik, "policies") && (
      <p className="text-[#E20000] text-xs mt-1">{ctx.formik.errors.policies}</p>
    )}
  </div>
);

// ---- The full create/edit page ----

const ParentCustomerForm = ({ ctx }) => (
  <>
    <NameField ctx={ctx} />
    <EmailField ctx={ctx} />
    <ClientPhoneField ctx={ctx} />
    <TeamEmailField ctx={ctx} />
    <LogoField ctx={ctx} />
    <AddressFields ctx={ctx} />
    <WebsiteField ctx={ctx} />
    {ctx.showInternal && <PoliciesField ctx={ctx} />}
  </>
);

const EntityForm = ({ ctx }) => (
  <>
    {ctx.showInternal && <BranchField ctx={ctx} />}
    <NameField ctx={ctx} />
    <EmailField ctx={ctx} />
    <ClientPhoneField ctx={ctx} />
    <ContactPersonField ctx={ctx} />
    {ctx.showInternal && (
      <>
        <ServiceTeamField ctx={ctx} />
        <OperationsTeamField ctx={ctx} />
      </>
    )}
    <RegistrationNumberField ctx={ctx} />
    <RegistrationCertificateField ctx={ctx} />
    <AddressFields ctx={ctx} />
    <WebsiteField ctx={ctx} />
    <AccountsFields ctx={ctx} />
    {ctx.showInternal && <BillingTermsFields ctx={ctx} />}
    <TaxRegistrationField ctx={ctx} />
    {ctx.showInternal && <PoliciesField ctx={ctx} />}
  </>
);

const CreateEditClientForm = ({ compactTop = false, isParentClient, ...contextProps }) => {
  const ctx = useClientFormContext(contextProps);

  return (
    <ClientFormGrid ctx={ctx} compactTop={compactTop}>
      {isParentClient ? <ParentCustomerForm ctx={ctx} /> : <EntityForm ctx={ctx} />}
    </ClientFormGrid>
  );
};

export default CreateEditClientForm;
